import type { ProductColorOption } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { cn } from '@/shared/ui';

/** 흰 바탕에서 경계가 사라지는 밝은 색인지 — 흰색 계열 견본만 테두리를 올린다. */
const isNearWhite = (colorCode: string): boolean =>
  ['#fff', '#ffffff', '#fefefe'].includes(colorCode.trim().toLowerCase());

interface ColorSwatchPickerProps {
  readonly options: readonly ProductColorOption[];
  readonly selectedId: string | null;
  readonly onSelect: (colorOptionId: string) => void;
}

/** 색상 선택 — 수급 불가 색상도 목록에 남기고 선택만 막는다(와이어프레임 원칙). */
export const ColorSwatchPicker = ({
  options,
  selectedId,
  onSelect,
}: ColorSwatchPickerProps): ReactElement => (
  <section aria-label="색상" className="space-y-2">
    <h2 className="text-sm font-semibold">색상</h2>
    <ul className="flex flex-wrap gap-3">
      {options.map((option) => (
        <li key={option.id} className="flex flex-col items-center gap-1">
          <button
            type="button"
            aria-label={option.isAvailable ? option.name : `${option.name} (수급 불가)`}
            aria-pressed={selectedId === option.id}
            // disabled 대신 aria-disabled — 포커스로 이유를 읽을 수 있게 남긴다.
            aria-disabled={!option.isAvailable}
            title={option.isAvailable ? option.name : `${option.name} (수급 불가)`}
            onClick={() => {
              if (option.isAvailable) {
                onSelect(option.id);
              }
            }}
            style={{ backgroundColor: option.colorCode }}
            className={cn(
              'size-8 rounded-full border border-foreground/20 transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              // 흰 상품색은 흰 바탕에서 경계가 사라진다 — 입력칸과 같은 Rule Gray 로 세운다.
              isNearWhite(option.colorCode) && 'border-input',
              selectedId === option.id && 'ring-2 ring-foreground ring-offset-2',
              option.isAvailable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50',
            )}
          />
          {/* 수급 불가는 취소선으로만 구분한다 — 글자색을 흐리면 대비가 깨진다. */}
          <span
            className={cn('text-xs text-muted-foreground', !option.isAvailable && 'line-through')}
          >
            {option.name}
          </span>
        </li>
      ))}
    </ul>
  </section>
);
